/*
 * music.js — reproductor del mp3 del intro (reemplaza a la cumbia sintetizada de cumbia.js).
 * ============================================================================
 * Toca un archivo de audio real (docs/ → public/audio/) durante el intro. El clic en "Entrar" es el
 * gesto de usuario que desbloquea la reproducción (los navegadores no dejan sonar audio sin interacción).
 * Fade-in corto al arrancar y fadeout controlado al cerrar (la canción NO se corta de golpe).
 *
 * Además enruta el audio por un AudioContext → AnalyserNode para exponer el ESPECTRO en vivo
 * (getLevels), que alimenta el ecualizador visual del intro. Como el archivo se sirve del mismo origen
 * (/audio/…), el análisis no queda "tainted" por CORS.
 *
 * La canción real dura minutos; el intro solo reproduce sus primeros ~22s y luego hace fadeout de 2s
 * (la línea de tiempo la controla intro.js). Al bajar el volumen, el espectro baja solo → el
 * ecualizador se encoge con el fadeout.
 */

export function createMusic(url, { volume = 0.9, fadeInSec = 0.6 } = {}) {
  const audio = new Audio(url);
  audio.preload = 'auto';
  audio.loop = false;

  // Grafo Web Audio (se crea en el primer start(), ya dentro del gesto de usuario).
  let ctx = null, analyser = null, freqData = null, srcNode = null;

  let raf = null;
  function cancelRamp() {
    if (raf) { cancelAnimationFrame(raf); raf = null; }
  }

  // Rampa lineal de volumen de `from` a `to` en `sec` segundos; `onEnd` al terminar.
  function ramp(from, to, sec, onEnd) {
    cancelRamp();
    const dur = Math.max(0.03, sec) * 1000;
    const t0 = performance.now();
    audio.volume = Math.max(0, Math.min(1, from));
    function step(now) {
      const k = Math.min((now - t0) / dur, 1);
      audio.volume = Math.max(0, Math.min(1, from + (to - from) * k));
      if (k < 1) raf = requestAnimationFrame(step);
      else { raf = null; if (onEnd) onEnd(); }
    }
    raf = requestAnimationFrame(step);
  }

  // Enruta el <audio> por el AnalyserNode. Idempotente (createMediaElementSource solo se puede una vez).
  function ensureGraph() {
    if (ctx) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      srcNode = ctx.createMediaElementSource(audio);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 128;                 // 64 bins de frecuencia
      analyser.smoothingTimeConstant = 0.75;  // suaviza el temblor entre frames
      freqData = new Uint8Array(analyser.frequencyBinCount);
      // source → analyser → salida (el volumen del elemento escala la señal antes de oírse)
      srcNode.connect(analyser);
      analyser.connect(ctx.destination);
    } catch (e) { ctx = null; analyser = null; freqData = null; }
  }

  return {
    /* Arranca desde el inicio con un fade-in corto. Debe llamarse dentro del gesto de clic. */
    start() {
      ensureGraph();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
      try { audio.currentTime = 0; } catch (e) { /* algunos navegadores lo bloquean hasta cargar */ }
      audio.volume = 0;
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* sin audio: el intro sigue en silencio */ });
      ramp(0, volume, fadeInSec);
    },
    /* Fadeout de `fadeSec` segundos y pausa al terminar (sin corte brusco). */
    stop(fadeSec = 0.7) {
      ramp(audio.volume, 0, fadeSec, () => { try { audio.pause(); } catch (e) {} });
    },
    /*
     * Devuelve `n` niveles [0..1] del espectro en vivo (graves→agudos), o null si no hay análisis.
     * Recorta el tercio superior de bins (casi mudo en una cumbia) para que las barras tengan vida.
     */
    getLevels(n) {
      if (!analyser || !freqData) return null;
      analyser.getByteFrequencyData(freqData);
      const usable = Math.max(1, Math.floor(freqData.length * 0.7));
      const out = new Array(n);
      for (let i = 0; i < n; i++) {
        const idx = Math.min(usable - 1, Math.floor((i / n) * usable));
        out[i] = freqData[idx] / 255;
      }
      return out;
    },
  };
}
